import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { map } from 'rxjs';

import { DebugEntry,DebugLogEntry, debugStore } from './debug.store';

@Component({
  selector: 'app-debug-panel',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="debug-wrap" *ngIf="(vm$ | async) as vm" [class.is-hidden]="!vm.enabled">
      <div class="debug-header">
        <div class="debug-title">DEBUG</div>
        <div class="debug-actions">
          <button type="button" (click)="clear()">Clear</button>
          <button type="button" (click)="hide()">Hide</button>
        </div>
      </div>

      <div class="debug-message" *ngIf="vm.message">{{ vm.message }}</div>

      <div class="debug-log" *ngFor="let l of vm.logs">
        <div class="debug-key">
          {{ l.level.toUpperCase() }}
          <span class="debug-time">· {{ l.ts | date:'HH:mm:ss' }}</span>
        </div>
        <div class="debug-msg">{{ l.message }}</div>
        <pre class="debug-value" *ngIf="l.data">{{ format(l.data) }}</pre>
      </div>

      <hr class="debug-sep" *ngIf="vm.logs.length && vm.entries.length" />

      <div class="debug-entry" *ngFor="let e of vm.entries">
        <div class="debug-key">
          {{ e.key }}
          <span class="debug-time">· {{ e.updatedAt | date:'HH:mm:ss' }}</span>
        </div>

        <pre class="debug-value">{{ format(e.value) }}</pre>
      </div>
    </div>
  `,
  styles: [`
    .debug-wrap{
      position: fixed;
      left: 8px;
      right: 8px;
      bottom: 8px;
      z-index: 9999;
      max-height: 40vh;
      overflow: auto;
      padding: 10px 12px;
      border-radius: 14px;
      background: rgba(0,0,0,0.78);
      color: #fff;
      font-size: 12px;
      box-shadow: 0 10px 28px rgba(0,0,0,0.28);
      backdrop-filter: blur(10px);
    }
    .is-hidden{ display:none; }
    .debug-header{
      display:flex;
      align-items:center;
      justify-content:space-between;
      margin-bottom: 6px;
    }
    .debug-title{ font-weight: 700; letter-spacing: .04em; }
    .debug-actions button{
      border:0;
      background: rgba(255,255,255,0.12);
      color:#fff;
      padding: 4px 8px;
      border-radius: 10px;
      margin-left: 6px;
      font-size: 12px;
    }
    .debug-message{
      margin: 6px 0 10px;
      opacity: .92;
    }
    .debug-entry{ margin-bottom: 10px; }
    .debug-key{ font-weight: 600; margin-bottom: 4px; }
    .debug-time{ opacity: .7; font-weight: 400; margin-left: 6px; }
    .debug-value{
      margin: 0;
      padding: 8px;
      border-radius: 12px;
      background: rgba(255,255,255,0.10);
      overflow:auto;
      white-space: pre-wrap;
      word-break: break-word;
    }
      .debug-log{ margin-bottom: 10px; }
  .debug-msg{ opacity: .92; margin: 0 0 6px; }
  .debug-sep{
    border: 0;
    height: 1px;
    background: rgba(255,255,255,0.12);
    margin: 10px 0;
  }
  `]
})
export class DebugPanelComponent {
  /** Optional: show even if disabled (useful on dev builds) */
  @Input() force = false;

  vm$ = debugStore.state$.pipe(
    map(s => ({
      enabled: this.force ? true : s.enabled,
      message: s.message,
      logs: (s.logs ?? []) as DebugLogEntry[],
      entries: Object.values(s.entries).sort((a, b) => b.updatedAt - a.updatedAt) as DebugEntry[]
    }))
  );

  hide() { debugStore.setEnabled(false); }
  clear() { debugStore.clear(); }

  format(v: unknown): string {
    if (v === null) return 'null';
    if (v === undefined) return 'undefined';

    // Strings: show as-is
    if (typeof v === 'string') return v;

    // Numbers/bools
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);

    // Everything else: JSON pretty
    try {
      return JSON.stringify(v, (key, value) => {
        if (value instanceof Error) {
          return { name: value.name, message: value.message, stack: value.stack };
        }
        return value;
      }, 2);
    } catch {
      // circular refs etc
      return Object.prototype.toString.call(v);
    }
  }
}
